import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Gift, Trash2, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function PromoCodesPage() {
  const [codes, setCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCodes = useCallback(async () => {
    try {
      const r = await api.get('/promo-codes?limit=200');
      setCodes(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Blad ladowania'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Skopiowano do schowka');
  };

  const handleDelete = async (id) => {
    try {
      await api.delete('/promo-codes/' + id);
      toast.success('Usunieto');
      fetchCodes();
    } catch { toast.error('Blad'); }
  };

  return (
    <div className="space-y-6" data-testid="promo-codes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Gift className="h-6 w-6 text-primary" />
            Promocyjne kody
          </h1>
          <p className="text-muted-foreground text-sm">SMS-y od IQOS i innych nadawcow promocyjnych ({total})</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchCodes}>
          <RefreshCw className="h-4 w-4 mr-2" /> Odswiez
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : codes.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Gift className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Brak kodow promocyjnych</p>
              <p className="text-sm mt-2">SMS-y od IQOS pojawia sie tu automatycznie</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Nadawca</TableHead>
                  <TableHead>Od numeru</TableHead>
                  <TableHead className="w-1/2">Tresc SMS</TableHead>
                  <TableHead>Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {c.created_at ? new Date(c.created_at).toLocaleString('pl-PL') : '?'}
                    </TableCell>
                    <TableCell className="font-medium text-primary">{c.sender || 'IQOS'}</TableCell>
                    <TableCell className="text-xs font-mono">{c.phone_from || '?'}</TableCell>
                    <TableCell className="text-sm">
                      <div className="max-w-md">
                        {c.message || '?'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleCopy(c.message || '')}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(c.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
